import { BaseHandler } from './base-handler';
import { TaskResponse } from '../types';
import {
  createAutomation,
  listAutomations,
  toggleAutomation,
  deleteAutomation,
  AutomationCreateParams,
} from '../../agent/tools/automationTools';

/**
 * Handler for automation-related bot intents.
 * Routes create_automation, list_automations, toggle_automation, delete_automation.
 */
export class AutomationHandler extends BaseHandler {
  intent = 'automation';

  async execute(params: Record<string, unknown>, context?: Record<string, unknown>): Promise<TaskResponse> {
    const action = (context?.action as string) || (params.action as string) || 'list_automations';
    console.log(`[🤖 AUTOMATION HANDLER] Routing action: ${action}`, params);

    switch (action) {
      case 'create_automation':
        return this.handleCreate(params);
      case 'list_automations':
        return this.handleList();
      case 'toggle_automation':
      case 'enable_automation':
      case 'disable_automation':
        return this.handleToggle(params, action);
      case 'delete_automation':
        return this.handleDelete(params);
      default:
        return this.handleList();
    }
  }

  private async handleCreate(params: Record<string, unknown>): Promise<TaskResponse> {
    try {
      // Parse params from intent engine or LLM
      const name = (params.name as string) || (params.automation_name as string) || 'New Automation';
      
      // Robust trigger parsing (handles strings or objects from LLM)
      let triggerType = 'new_lead';
      let triggerConfig: Record<string, unknown> = {};

      if (typeof params.trigger === 'object' && params.trigger !== null) {
        const t = params.trigger as any;
        triggerType = t.type || t.triggerType || 'new_lead';
        triggerConfig = { ...triggerConfig, ...(t.config || {}) };
      } else {
        triggerType = (params.trigger_type as string) || (params.trigger as string) || 'new_lead';
      }

      if (params.trigger_status) triggerConfig.status = params.trigger_status;
      if (params.trigger_city) triggerConfig.city = params.trigger_city;
      if (params.threshold) triggerConfig.threshold = params.threshold;

      const actions: AutomationCreateParams['actions'] = [];

      // Parse action from params
      const actionType = (params.action_type as string) || (params.automation_action as string) || '';
      if (actionType) {
        const actionConfig: Record<string, unknown> = {};
        if (params.message) actionConfig.message = params.message;
        if (params.subject) actionConfig.subject = params.subject;
        if (params.task_title) actionConfig.taskTitle = params.task_title;
        if (params.delay_seconds) actionConfig.duration = params.delay_seconds;
        if (params.status) actionConfig.status = params.status;
        if (params.priority) actionConfig.priority = params.priority;
        actions.push({ type: actionType, config: actionConfig });
      }

      // Parse additional actions if provided as array
      const extraActions = params.actions as Array<Record<string, unknown>> | undefined;
      if (Array.isArray(extraActions)) {
        for (const a of extraActions) {
          actions.push({
            type: (a.type as string) || 'notify',
            config: a.config as Record<string, unknown> || {},
          });
        }
      }

      // If no actions were parsed, add a default notification
      if (actions.length === 0) {
        actions.push({
          type: 'notify',
          config: { message: (params.message as string) || 'Automation triggered!' },
        });
      }

      // Parse conditions
      const conditions: AutomationCreateParams['conditions'] = [];
      if (params.condition_field && params.condition_value) {
        conditions.push({
          field: params.condition_field as string,
          operator: (params.condition_operator as string) || 'equals',
          value: params.condition_value as string,
        });
      }

      // Parse additional conditions if provided as array
      const extraConditions = params.conditions as Array<Record<string, unknown>> | undefined;
      if (Array.isArray(extraConditions)) {
        for (const c of extraConditions) {
          conditions.push({
            field: (c.field as string) || '',
            operator: (c.operator as string) || 'equals',
            value: (c.value as string) || '',
          });
        }
      }

      const createParams: AutomationCreateParams = {
        name,
        trigger: { type: triggerType, config: triggerConfig },
        actions,
        conditions: conditions.length > 0 ? conditions : undefined,
      };

      const result = await createAutomation(createParams);

      if (result.success) {
        // Build a human-readable summary
        const triggerDesc = getTriggerDescription(triggerType, triggerConfig);
        const actionDescs = actions.map(a => getActionDescription(a.type, a.config || {})).join(', then ');
        const summary = `✅ **Automation Created: "${name}"**\n\n` +
          `**When:** ${triggerDesc}\n` +
          `**Then:** ${actionDescs}\n\n` +
          `It's now **active** and will run automatically. You can manage it in the Automations Hub.`;
        
        return this.wrapSuccess(summary, { automationId: result.automationId });
      }
      
      return this.wrapError(result.message);
    } catch (err) {
      console.error('[AutomationHandler] Create error:', err);
      return this.wrapError('Failed to create automation. Please try again.');
    }
  }

  private async handleList(): Promise<TaskResponse> {
    const result = await listAutomations();
    return result.success
      ? this.wrapSuccess(result.message, { automations: result.automations })
      : this.wrapError(result.message);
  }

  private async handleToggle(params: Record<string, unknown>, action: string): Promise<TaskResponse> {
    const nameOrId = (params.name as string) || (params.automation_name as string) || (params.id as string) || '';
    if (!nameOrId) return this.wrapError('Please specify which automation to toggle.');
    
    const enabled = action !== 'disable_automation' && !(params.disable === true);
    const result = await toggleAutomation(nameOrId, enabled);
    return result.success ? this.wrapSuccess(result.message) : this.wrapError(result.message);
  }

  private async handleDelete(params: Record<string, unknown>): Promise<TaskResponse> {
    const nameOrId = (params.name as string) || (params.automation_name as string) || (params.id as string) || '';
    if (!nameOrId) return this.wrapError('Please specify which automation to delete.');
    
    const result = await deleteAutomation(nameOrId);
    return result.success ? this.wrapSuccess(result.message) : this.wrapError(result.message);
  }
}

// --- Description Helpers ---

function getTriggerDescription(type: string, config: Record<string, unknown>): string {
  switch (type) {
    case 'new_lead': return 'A new lead is added';
    case 'status_change': return `Lead status changes${config.status ? ` to "${config.status}"` : ''}`;
    case 'sms_received': return 'An SMS is received';
    case 'email_received': return 'An Email is received';
    case 'webhook_received': return 'A Webhook is received';
    case 'high_score': return `Lead score exceeds ${config.threshold || 80}`;
    case 'lead_score_changed': return `Lead score changes`;
    case 'lead_tag_added': return `A tag is added to the lead`;
    case 'task_overdue': return 'A task becomes overdue';
    case 'task_completed': return 'A task is completed';
    case 'birthday': return "It's a client's birthday";
    case 'offer_submitted': return 'An offer is submitted';
    case 'inactivity': return 'A lead has been inactive';
    case 'scheduled':
    case 'daily': 
      return 'Scheduled (Once a day)';
    case 'monthly_run': return 'On the 1st of the month';
    default: return type;
  }
}

function getActionDescription(type: string, config: Record<string, unknown>): string {
  switch (type) {
    case 'send_sms': return `send an SMS${config.message ? ` ("${(config.message as string).slice(0, 50)}...")` : ''}`;
    case 'send_email': return `send an email${config.subject ? ` with subject "${config.subject}"` : ''}`;
    case 'add_task': return `create a task${config.taskTitle ? ` "${config.taskTitle}"` : ''}`;
    case 'notify': return 'send a notification';
    case 'delay': return `wait ${formatDelay(config.duration as number)}`;
    case 'update_status': return `update lead status to "${config.status || '...'}"`;
    case 'update_score': return `update lead score to ${config.scoreValue || '...'}`;
    case 'create_note': return `add a note to the lead`;
    case 'add_tag': return `add a tag to the lead`;
    case 'assign_lead': return `assign the lead`;
    case 'call_webhook': return `call a webhook`;
    case 'send_slack': return `send a Slack message`;
    default: return type;
  }
}

function formatDelay(seconds: number | undefined): string {
  if (!seconds) return 'a while';
  if (seconds >= 86400) return `${Math.round(seconds / 86400)} day(s)`;
  if (seconds >= 3600) return `${Math.round(seconds / 3600)} hour(s)`;
  if (seconds >= 60) return `${Math.round(seconds / 60)} minute(s)`;
  return `${seconds} second(s)`;
}
